import React, { useMemo } from "react";
import { SearchSelect, overlayContentSize, type SearchSelectProps } from "@shared/ui/search-select";
import { Button } from "@shared/ui/button";
import { formatUnresolvedIdForDisplay } from "../formatUnresolvedIdForDisplay";
import type { UseBusinessSearchSelectParams } from "./useBusinessSearchSelect";
import { useBusinessSearchSelect } from "./useBusinessSearchSelect";
import { Icon, StatePlaceholder } from "@omega/ui";
import { TypingIcon } from "@omega/icons";

type IdValue = string | number;

export type BusinessSearchSelectProps<T extends object, Id extends IdValue | object = IdValue> = Omit<
    SearchSelectProps<T>,
    "options" | "value" | "getOptionLabel" | "getSelectedLabel" | "isLoading" | "isRefreshing" | "onSearch" | "idleAction"
> &
    Pick<
        UseBusinessSearchSelectParams<T, Id>,
        | "queryKeyBase"
        | "loadOptions"
        | "loadById"
        | "getId"
        | "getOptionLabel"
        | "getSelectedLabel"
        | "minSearchLength"
        | "debounceDelay"
        | "disabled"
        | "value"
        | "onChange"
        | "transformOptions"
        | "isValueId"
        | "isIdEqual"
        | "byIdQueryEnabled"
        | "byIdStaleTime"
    > & {
        /**
         * Доп. blocking-загрузка поверх search query. \
         * Идёт в `isLoading`, не в soft `isRefreshing`.
         */
        additionalIsLoading?: boolean;
        /**
         * Разрешить явную загрузку полного списка при активном `minSearchLength`. \
         * В idle рендерится `idleAction` («Показать весь список») с density от `surface`.
         * @default true
         * @warning Отключайте (`allowShowAll={false}`), если API/фильтры возвращают слишком большой список.
         * @see {@link overlayContentSize}
         */
        allowShowAll?: boolean;
        /** Подпись кнопки полного списка. @default "Показать весь список" */
        showAllText?: string;
    };

/**
 * Бизнес SearchSelect: поиск + resolve id через {@link useBusinessSearchSelect}. \
 * Content опций — `getOptionLabel` / `getSelectedLabel`; per-surface UI — `optionSlots` (merge, не replace). \
 * Idle-placeholder и «Показать весь список» — через `idleText` / package `idleAction` слоты
 * (адаптация `sm`/`md` по `surface`). \
 * Overlay loading: blocking (`isSearchLoading`) vs soft refresh (`isRefreshing`) — как в NBP ComboboxField.
 */
export function BusinessSearchSelect<T extends object, Id extends IdValue | object = IdValue>(
    props: BusinessSearchSelectProps<T, Id>,
) {
    const {
        queryKeyBase,
        loadOptions,
        loadById,
        getId,
        getOptionLabel,
        getSelectedLabel,
        value,
        onChange,
        minSearchLength,
        debounceDelay,
        transformOptions,
        disabled: disabledProp,
        isValueId,
        isIdEqual,
        byIdQueryEnabled,
        byIdStaleTime,
        additionalIsLoading = false,
        allowShowAll = true,
        showAllText = "Показать весь список",
        statusSlot: statusSlotFromConsumer,
        idleText,
        ...searchSelectProps
    } = props;

    const {
        options,
        resolvedValue,
        isResolving,
        isUnresolved,
        unresolvedValueId,
        handleSearch,
        handleShowAll,
        isSearchLoading,
        isSearchFetching,
        handleChange,
        getOptionLabel: getOptionLabelFromHook,
        getSelectedLabel: getSelectedLabelFromHook,
        idle,
    } = useBusinessSearchSelect<T, Id>({
        value,
        minSearchLength,
        debounceDelay,
        queryKeyBase,
        loadOptions,
        loadById,
        getId,
        getOptionLabel,
        getSelectedLabel,
        transformOptions,
        onChange,
        disabled: disabledProp === true,
        isValueId,
        isIdEqual,
        byIdQueryEnabled,
        byIdStaleTime,
    });

    const defaultStatusSlot = useMemo(() => {
        if (isResolving) return "Загрузка...";
        if (isUnresolved && unresolvedValueId != null) {
            return `Не найдено (код: ${formatUnresolvedIdForDisplay(unresolvedValueId)})`;
        }
        return undefined;
    }, [isResolving, isUnresolved, unresolvedValueId]);

    const statusSlot = statusSlotFromConsumer !== undefined ? statusSlotFromConsumer : defaultStatusSlot;

    const blockingSearchLoading = isSearchLoading === true || additionalIsLoading === true;
    const backgroundSearchRefreshing =
        isSearchFetching === true && isSearchLoading === false && additionalIsLoading === false;

    return (
        <SearchSelect<T>
            {...searchSelectProps}
            options={options}
            value={resolvedValue}
            getOptionKey={(option) => {
                const id = getId(option);
                if (typeof id === "string" || typeof id === "number") {
                    return id;
                }
                if (typeof id === "boolean") {
                    return id ? "true" : "false";
                }
                return getOptionLabelFromHook(option);
            }}
            getOptionLabel={getOptionLabelFromHook}
            getSelectedLabel={getSelectedLabelFromHook}
            isLoading={blockingSearchLoading}
            isRefreshing={backgroundSearchRefreshing}
            onSearch={handleSearch}
            onChange={handleChange}
            disabled={disabledProp}
            statusSlot={statusSlot}
            idleText={
                !idle
                    ? undefined
                    : (idleText ??
                      (({ surface }) => (
                          <StatePlaceholder
                              size={overlayContentSize(surface)}
                              title={
                                  minSearchLength != null
                                      ? `Введите минимум ${minSearchLength} символа`
                                      : "Начните вводить для поиска"
                              }
                              description="Начните вводить для поиска"
                              icon={<Icon icon={TypingIcon} size="100%" />}
                          />
                      )))
            }
            idleAction={
                !idle || !allowShowAll
                    ? undefined
                    : ({ surface }) => (
                          <Button type="button" variant="ghost" size={overlayContentSize(surface)} onClick={handleShowAll}>
                              {showAllText}
                          </Button>
                      )
            }
        />
    );
}
